import { cn } from "@/lib/utils";

export type LogoPlacement =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

interface LogoPlacementOption {
  value: LogoPlacement;
  label: string;
  description: string;
}

const placementOptions: LogoPlacementOption[] = [
  {
    value: "top-left",
    label: "Top Left",
    description: "Logo in the top-left corner",
  },
  {
    value: "top-right",
    label: "Top Right",
    description: "Logo in the top-right corner",
  },
  {
    value: "bottom-left",
    label: "Bottom Left",
    description: "Logo in the bottom-left corner",
  },
  {
    value: "bottom-right",
    label: "Bottom Right",
    description: "Logo in the bottom-right corner",
  },
];

interface LogoPlacementSelectorProps {
  value?: LogoPlacement;
  onChange: (value: LogoPlacement) => void;
  disabled?: boolean;
  className?: string;
  logoUrl?: string;
  referenceImageUrl?: string; // Add reference image URL prop
}

interface PlacementPreviewProps {
  placement: LogoPlacement;
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
  label: string;
  logoUrl?: string;
  referenceImageUrl?: string;
}

function PlacementPreview({
  placement,
  isSelected,
  onClick,
  disabled,
  label,
  logoUrl,
  referenceImageUrl,
}: PlacementPreviewProps) {
  const getLogoPosition = (placement: LogoPlacement) => {
    switch (placement) {
      case "top-left":
        return "top-3 left-3";
      case "top-right":
        return "top-3 right-3";
      case "bottom-left":
        return "bottom-3 left-3";
      case "bottom-right":
        return "bottom-3 right-3";
      default:
        return "top-3 left-3";
    }
  };

  return (
    <div className="text-center space-y-2">
      <div
        className={cn(
          "relative w-full aspect-[3/2] border rounded-lg cursor-pointer transition-all",
          "bg-gradient-to-br from-slate-100 to-slate-200",
          "hover:shadow-md",
          isSelected
            ? "border-primary ring-2 ring-primary"
            : "border-muted-foreground/30 hover:border-primary/60",
          disabled && "opacity-50 cursor-not-allowed",
        )}
        onClick={onClick}
      >
        {/* Content area - show reference image if available, otherwise show placeholder */}
        {referenceImageUrl ? (
          <div className="absolute inset-3 overflow-hidden">
            <img
              src={referenceImageUrl}
              alt="Reference content"
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="absolute inset-3 bg-white/60 rounded-sm border border-dashed border-gray-400 flex items-center justify-center">
            <span className="text-sm text-gray-500">Content</span>
          </div>
        )}

        {/* Logo - actual logo if available, otherwise placeholder */}
        <div
          className={cn(
            "absolute w-20 h-14 flex items-center justify-center",
            getLogoPosition(placement),
          )}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Logo"
              className="w-full h-full object-contain p-1"
            />
          ) : (
            <div className="w-full h-full bg-primary rounded-md flex items-center justify-center">
              <div className="w-10 h-7 bg-white rounded-sm"></div>
            </div>
          )}
        </div>
      </div>

      <p className="text-sm font-medium">{label}</p>
    </div>
  );
}

export function LogoPlacementSelector({
  value,
  onChange,
  disabled = false,
  className,
  logoUrl,
  referenceImageUrl,
}: LogoPlacementSelectorProps) {
  return (
    <div className={cn("space-y-4", className)}>
      {value && (
        <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-primary">
              Selected Placement:
            </span>
            <span className="text-sm font-medium">
              {placementOptions.find((opt) => opt.value === value)?.label}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {placementOptions.find((opt) => opt.value === value)?.description}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {placementOptions.map((option) => (
          <PlacementPreview
            key={option.value}
            placement={option.value}
            label={option.label}
            isSelected={value === option.value}
            onClick={() => !disabled && onChange(option.value)}
            disabled={disabled}
            logoUrl={logoUrl}
            referenceImageUrl={referenceImageUrl}
          />
        ))}
      </div>
    </div>
  );
}
